import {
  ArrowBack,
  CheckCircleOutline,
  ChevronRight,
  DownloadOutlined,
  HomeWorkOutlined,
  PersonOutline,
  Schedule,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import LendmarqLoanTermsSheetPDF from "../../../_src_shared/pdf/LendmarqLoanTermsSheetPDF/LendmarqLoanTermsSheetPDF";
import { useEndpointHook } from "../../../hooks/useEndpointHook";
import Flx from "../../layout/Flx";
import PdfPreviewer from "../../pdf/components/PdfPreviewer";
import PdfDownloadSection from "../../pdf/rendering/PdfDownloadSection";
import TermsSheetPDF from "../../pdf/TermsSheetPDF";
import Htag from "../../typography/Htag";
import Txt from "../../typography/Txt";
import { grey } from "@mui/material/colors";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";

// ── Helpers ──

const FinancingRequestTermsSheetTab = ({ dealId }) => {
  const {
    loading,
    userAcceptLetterOfIntent,
    viewLetterOfIntent,
    getProfile,
    getBorrowerFinancingApplications,
  } = useEndpointHook();
  const profile = useSelector((state) => state?.profile);

  const [selected, setSelected] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Determine the single term sheet to display:
  // 1. If accepted exists for this deal, show it
  // 2. Otherwise, show the most recent pending
  const activeTermSheet = useMemo(() => {
    const accepted = (profile?.acceptedTermSheets || []).find(
      (ts) => ts?.dealId === dealId,
    );
    if (accepted) return accepted;

    const pending = (profile?.pendingTermSheets || [])
      .filter((ts) => ts.dealId === dealId)
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

    return pending[0] || null;
  }, [profile?.acceptedTermSheets, profile?.pendingTermSheets, dealId]);

  // Auto-fetch the full detail whenever the active term sheet changes
  const activeId = activeTermSheet?._id;
  useMemo(() => {
    if (!activeId) {
      setSelected(null);
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    viewLetterOfIntent({
      termSheetId: activeId,
      onSuccessFn: (d) => {
        setSelected(d);
        setInitialLoading(false);
      },
      onFailFn: () => {
        setInitialLoading(false);
      },
    });
  }, [activeId]);

  const handleAcceptConfirm = useCallback(() => {
    userAcceptLetterOfIntent({
      termSheetId: selected._id,
      onSuccessFn: () => {
        getProfile({
          onSuccessFn: () => {
            getBorrowerFinancingApplications({});
          },
        });
      },
    });
  }, [selected?._id]);

  if (!activeTermSheet) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.disabled" }}>
          No term sheets for this loan.
        </Typography>
      </Paper>
    );
  }

  return (
    <Flx column g={1.5}>
      <TermSheetDetailView
        termSheet={selected}
        loading={loading || initialLoading}
        onAccept={() => setAcceptOpen(true)}
      />

      <AcceptDialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        termSheet={selected}
        loading={loading}
        onConfirm={handleAcceptConfirm}
      />
    </Flx>
  );
};

// ══════════════════════════════════════
// TERM SHEET DETAIL VIEW
// ══════════════════════════════════════

const TermSheetDetailView = ({
  termSheet,
  onBack,
  onAccept,

  loading,
}) => {
  const data = termSheet?.term_sheet_data || {};

  if (loading || !termSheet) {
    return (
      <Flx center sx={{ py: 6 }}>
        <CircularProgress />
      </Flx>
    );
  }

  return (
    <Fade in timeout={300}>
      <Container maxWidth="md" sx={{ px: 0 }}>
        {/* <Button
          startIcon={
            <ArrowBack className="thin" sx={{ fontSize: "18px !important" }} />
          }
          onClick={onBack}
          variant="text"
          color="grey"
          sx={{
            // textTransform: "none",
            color: "text.secondary",
            fontWeight: 400,
            mb: 2.5,
            px: 0,
            // "&:hover": { bgcolor: "transparent", color: "primary.main" },
          }}
        >
          Back to Term Sheets
        </Button> */}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Flx fw jb ac wrap g={1}>
            <Flx fw column>
              <Flx ac g={1} wrap>
                <Flx ac g={1} sx={{ flexGrow: 1 }}>
                  <Htag variant="h3" sx={{ fontWeight: 700 }}>
                    Term Sheet Review
                  </Htag>

                  {termSheet?.preliminaryTermSheet && (
                    <Chip
                      label="Preliminary"
                      size="small"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        bgcolor: "#E3F2FD",
                        color: "#1565C0",
                      }}
                    />
                  )}
                  {termSheet?.isAccepted && (
                    <Chip
                      label={
                        termSheet?.isAccepted ? "Accepted" : "Pending Review"
                      }
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        bgcolor: termSheet?.isAccepted ? "#E8F5E9" : "#FFF8E1",
                        color: termSheet?.isAccepted ? "#2E7D32" : "#F57F17",
                      }}
                    />
                  )}
                </Flx>
                <Flx ac g={1}>
                  {termSheet?.isAccepted && (
                    <PdfDownloadSection
                      variant="button"
                      // buttonProps={{ size: "large" }}
                      document={<LendmarqLoanTermsSheetPDF />}
                      convertedData={termSheet?.term_sheet_data} // the full termSheet object from viewLetterOfIntent
                      filename="Loan_Terms.pdf"
                    />
                  )}
                </Flx>
              </Flx>

              {termSheet?.isAccepted ? null : (
                <Txt variant="body2" sx={{ color: "text.secondary" }}>
                  Please review the proposed loan terms below. If you agree,
                  click "Accept Terms" to proceed with underwriting and loan
                  document preparation.
                </Txt>
              )}
            </Flx>
          </Flx>
        </Box>
        {termSheet?.isAccepted ? null : (
          <Flx ac wrap fw g={1} sx={{ mt: 2, mb: 4 }}>
            <PdfDownloadSection
              variant="button"
              buttonProps={{
                size: "large",
                sx: {
                  flexGrow: 1,
                  // flexBasis: "400px",
                },
              }}
              document={<LendmarqLoanTermsSheetPDF />}
              convertedData={termSheet?.term_sheet_data} // the full termSheet object from viewLetterOfIntent
              filename="Loan_Terms.pdf"
            />
            <Button
              variant="contained"
              color="success"
              size="large"
              sx={{ flexGrow: 1 }}
              startIcon={<CheckCircleOutline />}
              onClick={onAccept}
            >
              Accept Terms
            </Button>
          </Flx>
        )}

        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 2.5,
            py: 2,
            mb: 2,
          }}
        >
          {/* <Typography sx={sectionLabelSx}>Loan Terms</Typography> */}

          <DetailRow
            label={"Status"}
            value={termSheet?.isAccepted ? "Accepted" : "Pending Approval"}
          />
          {termSheet?.isAccepted && (
            <>
              <DetailRow label={"Accepted By"} value={termSheet?.acceptedBy} />
              <DetailRow
                label={"Accepted Date"}
                value={formatDateTime(termSheet?.acceptedDateTime)}
              />
            </>
          )}
          <DetailRow label={"Created By"} value={termSheet?.createdBy} />
          <DetailRow
            label={"Created Date"}
            value={formatDateTime(termSheet?.createdDate)}
          />
        </Paper>

        <Flx column g={1} sx={{ mt: 6 }}>
          <Flx fw jb ac g={1}>
            <Htag variant="h3" sx={{ fontWeight: 700 }}>
              Term Sheet Preview
            </Htag>
          </Flx>
          <Box
            sx={{
              border: `1px solid ${grey[200]}`,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <PdfPreviewer>
              <LendmarqLoanTermsSheetPDF convertedData={data} />
            </PdfPreviewer>
          </Box>
        </Flx>
      </Container>
    </Fade>
  );
};

// ══════════════════════════════════════
// ACCEPT DIALOG
// ══════════════════════════════════════

const AcceptDialog = ({ open, onClose, termSheet, onConfirm, loading }) => {
  const [confirmed, setConfirmed] = useState(false);
  const data = termSheet?.term_sheet_data || {};
  const address =
    data.subjectProperties?.[0]?.address?.fullAddress || "this property";

  const handleConfirm = () => {
    setConfirmed(true);
    setTimeout(() => {
      onConfirm();
      setConfirmed(false);
    }, 2000);
  };

  const handleClose = () => {
    if (!confirmed) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      {confirmed ? (
        <Box sx={{ textAlign: "center", py: 5, px: 3 }}>
          <Fade in timeout={400}>
            <Box>
              <CheckCircleOutline sx={{ fontSize: 56, color: "#2E7D32" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                Terms Accepted
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Your acceptance has been submitted. The Lendmarq team will be in
                touch with next steps and closing documents.
              </Typography>
              <LinearProgress
                sx={{
                  mt: 2,
                  mx: "auto",
                  width: 120,
                  borderRadius: 2,
                  bgcolor: "#E8F5E9",
                  "& .MuiLinearProgress-bar": { bgcolor: "#2E7D32" },
                }}
              />
            </Box>
          </Fade>
        </Box>
      ) : (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ bgcolor: "#E8F5E9", width: 40, height: 40 }}>
                <CheckCircleOutline sx={{ color: "#2E7D32" }} />
              </Avatar>
              <Htag variant="h1" sx={{ fontWeight: 700 }}>
                Accept Term Sheet
              </Htag>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2, lineHeight: 1.7 }}
            >
              By accepting, you agree to the proposed loan terms for{" "}
              <strong>{address}</strong> and authorize Lendmarq to proceed with
              underwriting and preparation of definitive loan documents.
            </Typography>
            <Paper
              elevation={0}
              sx={{ bgcolor: "#F5F5F5", p: 2, borderRadius: 1.5, mb: 1 }}
            >
              {[
                { label: "Loan Amount", value: fmt(data.totalLoanAmount) },
                {
                  label: "Rate",
                  value:
                    data.interestRate != null
                      ? fmtPct(data.interestRate)
                      : null,
                },
                { label: "Term", value: data.loanTerm || null },
                { label: "Product", value: data.loanProductType || null },
              ]
                .filter((r) => r.value != null)
                .map((r) => (
                  <Box
                    key={r.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.75,
                      "&:last-child": { mb: 0 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {r.label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {r.value}
                    </Typography>
                  </Box>
                ))}
            </Paper>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                display: "block",
                mt: 1.5,
                lineHeight: 1.55,
              }}
            >
              Acceptance does not constitute a binding commitment to lend and
              remains subject to Lendmarq's full underwriting process, credit
              approval, and execution of definitive loan documents.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button color="error" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              loading={loading}
              variant="contained"
              color="success"
              onClick={onConfirm}
            >
              Confirm Acceptance
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// ══════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════

const fmt = (n) => {
  if (n == null) return null;
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const fmtPct = (n) => {
  if (n == null) return null;
  return `${Number(n).toFixed(2)}%`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Field config — only fields present in term_sheet_data with a value get rendered
const FIELD_CONFIG = [
  { key: "loanProductType", label: "Loan Product Type" },
  { key: "loanPurpose", label: "Loan Purpose" },
  { key: "totalLoanAmount", label: "Total Loan Amount", format: "currency" },
  {
    key: "initialLoanAmount",
    label: "Initial Loan Amount",
    format: "currency",
  },
  { key: "purchasePrice", label: "Purchase Price", format: "currency" },
  { key: "asIsValue", label: "As-Is Value", format: "currency" },
  {
    key: "constructionBudget",
    label: "Construction Budget",
    format: "currency",
  },
  {
    key: "constructionHoldback",
    label: "Construction Holdback",
    format: "currency",
  },
  { key: "interestRate", label: "Interest Rate", format: "percent" },
  { key: "interestType", label: "Interest Type" },
  { key: "loanTerm", label: "Loan Term" },
  { key: "amortization", label: "Amortization" },
  { key: "loanToValue", label: "Loan-to-Value (LTV)", format: "percent" },
  { key: "loanToCost", label: "Loan-to-Cost (LTC)", format: "percent" },
  { key: "arvLtv", label: "ARV LTV", format: "percent" },
  { key: "lienPosition", label: "Lien Position" },
  { key: "occupancy", label: "Occupancy" },
  { key: "propertyType", label: "Property Type" },
  { key: "paymentReserve", label: "Payment Reserve" },
  { key: "exitStrategy", label: "Exit Strategy" },
  { key: "legalFee", label: "Legal Fee", format: "currency" },
  { key: "underwritingFee", label: "Underwriting Fee", format: "currency" },
];

const buildTermFields = (data) => {
  if (!data) return [];
  return FIELD_CONFIG.reduce((acc, { key, label, format }) => {
    const val = data[key];
    if (val == null || val === "") return acc;
    let display;
    if (format === "currency") display = fmt(val);
    else if (format === "percent") display = fmtPct(val);
    else display = String(val);
    if (display) acc.push({ label, value: display });
    return acc;
  }, []);
};

// ── Shared ──

const sectionLabelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "text.disabled",
  mb: 0.5,
};

const DetailRow = ({ label, value, highlight }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1.25,
      gap: 1,
      flexWrap: "wrap",
      borderBottom: 1,
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", fontSize: "0.84rem" }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        fontSize: "0.84rem",
        color: highlight ? "primary.main" : "text.primary",
        textAlign: "right",
        // maxWidth: "55%",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default FinancingRequestTermsSheetTab;
